/**
 * V2Preview — Admin-only living style-guide page.
 *
 * Renders each Phase-0 primitive with placeholder content plus the theme
 * toggle so QA can visually confirm the v2 tokens and dark/light modes.
 *
 * NOT linked from the main app nav. Reachable only via /v2-preview and gated
 * behind <AdminRoute> so end users cannot stumble in during the rollout.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, ChartBar, Award, TrendingUp } from "lucide-react";

import ThemeToggle from "@/components/v2/ThemeToggle";
import V2Hero from "@/components/v2/V2Hero";
import TestimonialCard from "@/components/v2/TestimonialCard";
import EmptyState from "@/components/v2/EmptyState";
import MilestoneCard from "@/components/v2/MilestoneCard";
import StatCallout from "@/components/v2/StatCallout";
import { useTheme } from "@/contexts/ThemeContext";

function SectionLabel({ children }) {
  return (
    <div className="text-v2-caption uppercase text-v2-textSubtle tracking-[0.14em] mb-3">
      {children}
    </div>
  );
}

export default function V2Preview() {
  const { effectiveTheme } = useTheme();
  return (
    <div
      data-testid="v2-preview-page"
      className="min-h-screen bg-v2-background text-v2-text"
    >
      {/* Top bar */}
      <header className="border-b border-v2-border bg-v2-surface">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link
            to="/dashboard"
            data-testid="v2-preview-back-link"
            className="inline-flex items-center gap-2 text-v2-body-sm text-v2-textMuted hover:text-v2-text"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-v2-caption uppercase text-v2-textSubtle hidden sm:inline">
              Theme: <span className="text-v2-textMuted normal-case">{effectiveTheme}</span>
            </span>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10 space-y-14">
        {/* Page intro */}
        <section>
          <div className="text-v2-caption uppercase text-v2-accentStrong mb-3">V2.0 · Phase 0</div>
          <h1
            className="text-v2-display-lg text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Design system preview
          </h1>
          <p className="mt-4 text-v2-body-lg text-v2-textMuted max-w-2xl">
            Foundation primitives for the V2.0 Premium Experience. Toggle themes above to preview
            each component in light and dark modes. Copy is placeholder — real content ships in
            Phases 1–3.
          </p>
        </section>

        {/* V2Hero */}
        <section>
          <SectionLabel>V2Hero</SectionLabel>
          <V2Hero
            eyebrow="Funding readiness"
            title="Get funding-ready in weeks, not years."
            subtitle="Analyze your credit story, repair blockers, and match with vetted funding partners — all in one workspace built for founders and homeowners."
            primaryCta={{ label: "Start free check", href: "#" }}
            secondaryCta={{ label: "How it works", href: "#" }}
            illustration={
              <div className="aspect-[4/3] rounded-v2-xl bg-v2-accentSoft border border-v2-border grid place-items-center text-v2-accentStrong">
                <span className="text-v2-caption uppercase">Illustration slot</span>
              </div>
            }
          />
        </section>

        {/* StatCallout row */}
        <section>
          <SectionLabel>StatCallout</SectionLabel>
          <div className="grid gap-4 md:grid-cols-4">
            <StatCallout
              value="72"
              label="Funding readiness"
              trend={{ direction: "up", delta: "+14 pts vs last month" }}
              variant="success"
              icon={<TrendingUp className="h-4 w-4" />}
            />
            <StatCallout
              value="3"
              label="Open disputes"
              variant="info"
              icon={<ChartBar className="h-4 w-4" />}
            />
            <StatCallout
              value="$14.5k"
              label="Estimated funding fit"
              trend={{ direction: "up", delta: "+$2.1k" }}
              variant="default"
            />
            <StatCallout
              value="1"
              label="Days late payment"
              trend={{ direction: "down", delta: "-2 vs prior" }}
              variant="warning"
            />
          </div>
        </section>

        {/* MilestoneCard row */}
        <section>
          <SectionLabel>MilestoneCard</SectionLabel>
          <div className="grid gap-4 md:grid-cols-3">
            <MilestoneCard
              status="completed"
              title="Removed 2 stale collections"
              completedDate="2026-01-14T00:00:00Z"
              metric={{ value: "+38 pts", label: "score" }}
              nextStep="Prepare EIN documentation"
            />
            <MilestoneCard
              status="in_progress"
              title="Draft dispute letter for Portfolio Recovery"
              metric={{ value: "3", label: "days left" }}
            />
            <MilestoneCard
              status="upcoming"
              title="Match with a funding partner"
              nextStep="Complete the profile to unlock"
            />
          </div>
        </section>

        {/* TestimonialCard row */}
        <section>
          <SectionLabel>TestimonialCard</SectionLabel>
          <div className="grid gap-4 md:grid-cols-2">
            <TestimonialCard
              featured
              quote="Informed rebuilt my credit story so investors would finally say yes."
              metric={{ value: "+94 pts", label: "credit score" }}
              name="Sarah M."
              role="Business owner · Atlanta, GA"
            />
            <TestimonialCard
              quote="I went from denied twice to approved for $22k in working capital in eight weeks."
              metric={{ value: "$22k", label: "funded" }}
              name="Marcus T."
              role="Contractor · Houston, TX"
            />
          </div>
        </section>

        {/* EmptyState */}
        <section>
          <SectionLabel>EmptyState</SectionLabel>
          <EmptyState
            heading="No credit report uploaded yet"
            description="Upload a PDF of your latest report to get an AI-powered readiness snapshot in under 60 seconds."
            primaryCta={{ label: "Upload report", href: "#", icon: Upload }}
            secondaryCta={{ label: "See a sample", href: "#" }}
            illustration={
              <div
                aria-hidden="true"
                className="mx-auto grid place-items-center rounded-v2-2xl bg-v2-accentSoft h-32 w-32 text-v2-accentStrong"
              >
                <Award className="h-12 w-12" />
              </div>
            }
          />
        </section>

        {/* Footer */}
        <section className="pt-8 border-t border-v2-border">
          <p className="text-v2-body-sm text-v2-textSubtle">
            V2.0 Phase 0 · Foundation only. Legacy V1.1 surfaces are unchanged.
          </p>
        </section>
      </main>
    </div>
  );
}
